import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { usePostLoginMutation, useSendOtpMutation } from "../../service/auth";
import { toast } from "react-toastify";
import { setCredentials } from "../../reducers/authSlice";
import { setToStorage, getFromStorage, removeFromStorage, STORAGE_KEYS } from "../../helpers";
import { useAppDispatch } from "../../hooks/store";
import Loader from "../../constants/Loader";
import { FormHelperText } from "@mui/material";
import { OTP_TYPE } from "../../utils/enums";
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}>
    {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
  </div>
);

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const [valueTabs, setValueTabs] = useState<number>(0);
  const dispatch = useAppDispatch();
  const [postLogin, { isLoading: isEmailLoading }] = usePostLoginMutation();
  const [sendOtp, { isLoading: isPhoneLoading }] = useSendOtpMutation();

  useEffect(() => {
    const rememberMe = getFromStorage("rememberMe") === "true";
    if (rememberMe) {
      const email = getFromStorage("rememberedEmail") || "";
      const password = getFromStorage("rememberedPassword") || "";
      formik.setValues({
        email,
        password,
        remember: true,
      });
    }
  }, []);

  const loginSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const phoneLoginSchema = Yup.object().shape({
    phone: Yup.string()
      .min(7, "Phone number must be at least 7 characters")
      .required("Phone number is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      try {
        const deviceToken = getFromStorage(STORAGE_KEYS.fcmToken) || "";
        const response = await postLogin({
          email: values.email,
          password: values.password,
          deviceToken,
        }).unwrap();
        if (response.statusCode === 200 || response.statusCode === 201) {
          toast.success(response.message || "Login successful");

          if (values.remember) {
            setToStorage("rememberedEmail", values.email);
            setToStorage("rememberedPassword", values.password);
            setToStorage("rememberMe", "true");
          } else {
            removeFromStorage("rememberedEmail");
            removeFromStorage("rememberedPassword");
            removeFromStorage("rememberMe");
          }
          if (response.data) {
            const { token, ...user } = response.data as any;
            dispatch(setCredentials({ user, token }));
            if (token) {
              setToStorage(STORAGE_KEYS.token, token);
            }
          }

          if (response?.data?.isProfileComplete || (response?.data as any)?.user?.isProfileComplete) {
            navigate("/dashboard");
          } else {
            navigate("/signup-form");
          }
        } else {
          toast.error(response.message || "Login failed");
        }
      } catch (error: any) {
        let errMsg = error?.data?.message || "Something went wrong";
        if (typeof errMsg === "string" && errMsg.toLowerCase().includes("validation error")) {
          errMsg = errMsg.split(":").pop()?.trim() || errMsg;
        }
        toast.error(errMsg);
      }
    },
  });

  const phoneFormik = useFormik({
    initialValues: {
      phone: "",
      dialCode: "91", // Default
    },
    validationSchema: phoneLoginSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          phone: values.phone.slice(values.dialCode.length),
          countryCode: "+" + values.dialCode,
          otpType: OTP_TYPE.LOGIN,
        };
        const response = await sendOtp(payload).unwrap();
        if (response.statusCode === 200 || response.statusCode === 201) {
          toast.success(response.message || "OTP sent successfully");
          navigate(`/otp?type=phoneLogin`, {
            state: {
              mobileNumber: payload.phone,
              countryCode: payload.countryCode,
            },
          });
        } else {
          toast.error(response.message || "Failed to send OTP");
        }
      } catch (error: any) {
        let errMsg = error?.data?.message || "Something went wrong";
        if (typeof errMsg === "string" && errMsg.toLowerCase().includes("validation error")) {
          errMsg = errMsg.split(":").pop()?.trim() || errMsg;
        }
        toast.error(errMsg);
      }
    },
  });

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const a11yProps = (index: number) => ({
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  });
  return (
    <AuthLayout>
      <Loader isLoad={isEmailLoading || isPhoneLoading} />
      <div className="authBox">
        <h2>Login</h2>
        <Box className="custom_tabs1" sx={{ width: "100%", mt: 2 }}>
          <Tabs
            value={valueTabs}
            onChange={handleChangeTabs}
            aria-label="view product tabs"
            className="custom_tabs_links">
            <Tab label="Email" {...a11yProps(0)} />
            <Tab label="Phone Number" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={valueTabs} index={0}>
          <form className="form" onSubmit={formik.handleSubmit}>
            <div className="control_group">
              <label htmlFor="email">Email Address</label>
              <TextField
                id="email"
                name="email"
                type="email"
                className="text_field"
                placeholder="Enter Email Address"
                fullWidth
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </div>

            <div className="control_group">
              <label htmlFor="password">Password</label>
              <TextField
                id="password"
                name="password"
                placeholder="Enter Password"
                className="text_field"
                fullWidth
                type={showPassword ? "text" : "password"}
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password && Boolean(formik.errors.password)
                }
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className="eye_btn">
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div className="control_group d_flex">
              <div className="remember_box">
                <FormControlLabel
                  control={
                    <Checkbox
                      name="remember"
                      checked={formik.values.remember}
                      onChange={formik.handleChange}
                    />
                  }
                  label="Remember me"
                />
              </div>
              <button
                type="button"
                className="anchor_link"
                onClick={() => navigate("/forgot-password")}>
                Forgot Password?
              </button>
            </div>

            <div className="form_btn">
              <Button
                type="submit"
                className="btn btn_primary w_100"
                variant="contained"
                disabled={isEmailLoading}>
                {isEmailLoading ? "Logging in..." : "Login"}
              </Button>
            </div>
          </form>
        </CustomTabPanel>
        <CustomTabPanel value={valueTabs} index={1}>
          <form className="form" onSubmit={phoneFormik.handleSubmit}>
            <div className="control_group">
              <label htmlFor="phone">Phone Number</label>
              <PhoneInput
                inputProps={{
                  id: "phone",
                  name: "phone",
                  placeholder: "Enter Phone Number",
                }}
                country={"in"}
                autoFormat={false}
                value={phoneFormik.values.phone}
                onChange={(value, data: any) => {
                  phoneFormik.setFieldValue("phone", value);
                  phoneFormik.setFieldValue("dialCode", data.dialCode);
                }}
                onBlur={() => phoneFormik.setFieldTouched("phone", true)}
              />
              {phoneFormik.touched.phone && phoneFormik.errors.phone && (
                <FormHelperText error>
                  {phoneFormik.errors.phone}
                </FormHelperText>
              )}
            </div>

            <div className="form_btn">
              <Button
                type="submit"
                className="btn btn_primary w_100"
                variant="contained"
                disabled={isPhoneLoading}>
                {isPhoneLoading ? "Sending OTP..." : "Login"}
              </Button>
            </div>
          </form>
        </CustomTabPanel>
        {/* <div className="sign_log">
          <p>
            Don't have an account ?
            <button style={{ border: "none", background: "transparent", cursor: "pointer", color: "#D3B88C" }} type="button" onClick={() => navigate("/sign-up")}>Sign up</button>
          </p>
        </div> */}
      </div>
    </AuthLayout>
  );
};

export default Login;
